'use client'

import { useUser } from '@clerk/nextjs'
import { usePathname } from 'next/navigation'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  type ComposerUploadsStore,
  createComposerUploadsStore,
} from '@/components/canvas/chat-composer-uploads'
import type { SelectedEmailInfo } from '@/components/canvas/selected-email-chips'
import { retainUnsavedListsOfOpenChats } from '@/components/canvas/unsaved-attachment-lists'
import type { UseCanvasChatsReturn } from '@/components/canvas/use-canvas-chats'
import { useCanvasChats } from '@/components/canvas/use-canvas-chats'
import { useChatAttachedEmails } from '@/components/canvas/use-chat-attached-emails'
import type {
  AttachedComposerImage,
  AttachImageResult,
} from '@/components/canvas/use-chat-attached-images'
import { useChatAttachedImages } from '@/components/canvas/use-chat-attached-images'
import { useOptionalBrandContext } from '@/contexts/brand-context'
import { useOrgContext } from '@/contexts/org-context'
import {
  type ChatParticipant,
  getParticipantDisplayName,
  getParticipantInitials,
} from '@/lib/chat-message-metadata'

import type { ChatPanelView } from './chat-panel-view-state'
import type { ChatSide } from './chat-side'
import { ElementEditTrackerProvider } from './element-edit-context'
import {
  type UseElementEditTrackerResult,
  useElementEditTracker,
} from './element-edit-tracker'
import { isExcludedChatPath } from './excluded-paths'
import { ForeignTabPruner } from './foreign-tab-pruner'
import { type ChatSurfaceState, useChatSurface } from './use-chat-surface'

/**
 * Layout-level chat context. The single owner of all chat state now
 * that the chat lives in the authenticated `(app)` layout instead of
 * the `(workspace)` route group:
 *
 *   - tab strip / active chat (`useCanvasChats`, unscoped — shared
 *     across every page),
 *   - the per-chat "attached to composer" email lattice (automations are
 *     no longer attached — the orchestrator derives the active automation
 *     from the user's URL/view state),
 *   - the element-edit tracker (also exposed via
 *     `ElementEditTrackerProvider` for existing canvas consumers),
 *   - current user / participants for message attribution,
 *   - a UI slice for the resizable panel (collapse state + imperative
 *     handle) so the header toggle and the shell agree.
 *
 * Ownership is INVERTED relative to the old design: the chat depends on
 * nothing from emails / automations. The emails + automations canvases
 * CONSUME attachment APIs from this context. Thread/`chatState` stays
 * here so a stream tick cannot recreate `CanvasLayoutContext` or
 * `AutomationCanvasLayoutContext` and commit the canvas trees.
 */
export type GlobalChatContextValue = {
  orgId: string
  userId: string
  brandId: string | undefined

  chatState: UseCanvasChatsReturn
  /**
   * The user's "New chat": opens a fresh tab and MOVES every attachment
   * staged on the active chat's composer — attached emails, brand images,
   * uploaded files — onto it, so the new conversation starts with what the
   * user lined up. Typed text stays with the chat it was written in, and
   * remix-source chips stay with the template page that owns them (it
   * re-stages them on the new chat itself). Returns the new chat id.
   *
   * Programmatic openers (template landing, insights) call
   * `chatState.addTab()` directly: they start their own context and must
   * not inherit the user's staged attachments.
   */
  startNewChat: () => string

  currentUser: ChatParticipant
  participants: Record<string, ChatParticipant>

  // Attached emails (per active chat tab).
  attachedEmails: Array<SelectedEmailInfo>
  attachEmail: (email: SelectedEmailInfo) => void
  attachManyEmails: (emails: ReadonlyArray<SelectedEmailInfo>) => void
  detachEmail: (emailId: string) => void
  clearAttachedEmails: () => void
  isEmailAttached: (emailId: string) => boolean
  attachedEmailIds: ReadonlySet<string>
  pruneAttachedEmails: (validEmailIds: ReadonlySet<string>) => void
  /**
   * Stage `email` as the SINGLE remix-source chip of `targetChatId`
   * (any other flagged chip on that chat is dropped). `targetChatId` need
   * not be the active chat — the templates page re-stamps whichever chat
   * the user switches to while a template is on screen. Returns true when
   * the email was already attached there beforehand.
   */
  setChatRemixSource: (
    targetChatId: string,
    email: SelectedEmailInfo
  ) => boolean
  /**
   * Undo `setChatRemixSource`. `shouldKeepChip` keeps the chip and only
   * strips its flag — for an email that was already staged from the
   * canvas before the template page stamped it.
   */
  clearChatRemixSource: (
    targetChatId: string,
    emailId: string,
    shouldKeepChip?: boolean
  ) => void
  /**
   * Drop every remix-source chip from `targetChatId`. Self-heal for a
   * template visit that ended without cleanup (a hard reload runs no React
   * teardown), so a stranded chip can't outlive the page that staged it.
   */
  clearAllChatRemixSources: (targetChatId: string) => void

  // Attached brand-asset images (per active chat tab). Staged by the
  // `/assets` gallery (card selection) and merged into the outgoing
  // user message as AI SDK `file` parts at submit; the lattice is the
  // single source of truth, so gallery cards derive their "selected"
  // state from `attachedImageUrls`.
  attachedImages: Array<AttachedComposerImage>
  attachedImageUrls: ReadonlySet<string>
  attachComposerImage: (image: AttachedComposerImage) => AttachImageResult
  detachComposerImage: (url: string) => void
  clearAttachedImages: () => void
  /**
   * Asset-deletion hygiene: drop `url` from the LIVE tab's lattice and
   * every stored lattice of the (org, brand) scope — safe to call from
   * closures held across a delete round-trip (unlike `detachComposerImage`,
   * which is scope-guarded against stale writes).
   */
  purgeComposerImageUrl: (url: string) => void

  elementEditTracker: UseElementEditTrackerResult

  // One-shot composer text seed (per chat). Written by surfaces that want
  // to pre-fill a chat's composer (the templates page restoring a
  // pre-sign-in draft). The chat thread whose `chatId` matches DISPLAYS
  // the seed while its local draft is empty and clears the slot on the
  // user's first edit or submit.
  composerSeed: { chatId: string; text: string } | null
  seedComposer: (chatId: string, text: string) => void
  clearComposerSeed: () => void

  // Per-chat composer drafts. The shell remounts the chat thread for
  // purely structural reasons (side flip, tab switch, split ⇄ full-page),
  // which would otherwise discard in-progress text; the thread reads its
  // draft once on mount and writes through on every edit so the text
  // survives any remount within this provider's lifetime.
  getComposerDraft: (chatId: string) => string
  setComposerDraft: (chatId: string, text: string) => void

  // Per-chat uploaded composer files — the same remount problem as the
  // drafts, plus in-flight uploads that must land on whichever chat holds
  // the file when they settle. Stable identity; the thread subscribes to
  // its own chat's slot (`useComposerUploads`).
  composerUploads: ComposerUploadsStore

  /**
   * Presentation: full-page (`/chat/<id>`, `/chats`) vs the split side
   * panel, the panel's persisted view (thread | list), side, width and
   * collapse state, and the navigations between them. Documented on
   * `ChatSurfaceState` (`./use-chat-surface`).
   */
} & ChatSurfaceState

const GlobalChatContext = createContext<GlobalChatContextValue | null>(null)

export function useGlobalChat(): GlobalChatContextValue {
  const ctx = useContext(GlobalChatContext)
  if (!ctx) {
    throw new Error('useGlobalChat must be used within a GlobalChatProvider')
  }
  return ctx
}

/** Safe variant — returns null outside the provider. */
export function useGlobalChatSafe(): GlobalChatContextValue | null {
  return useContext(GlobalChatContext)
}

export type GlobalChatProviderProps = {
  /**
   * Server-read `brew_chat_side` cookie. Passing it keeps the first paint
   * (and hydration) on the user's chosen edge — see `./chat-side`.
   */
  initialChatSide?: ChatSide
  /**
   * Server-read `brew_chat_view` cookie — same hydration argument for the
   * panel's list/thread view. See `./chat-panel-view-state`.
   */
  initialChatView?: ChatPanelView
  children: ReactNode
}

export function GlobalChatProvider({
  initialChatSide,
  initialChatView,
  children,
}: GlobalChatProviderProps) {
  const { orgId, userId } = useOrgContext()
  const brandCtx = useOptionalBrandContext()
  const brandId = brandCtx?.brandId
  const isBrandSwitching = brandCtx?.isSwitching ?? false
  const pathname = usePathname()

  // Single shared, unscoped chat — tabs + active chat persist across
  // EVERY authenticated page (the chat now lives at the layout level).
  const chatState = useCanvasChats(orgId, brandId)
  const {
    activeChatId,
    adoptActiveChat,
    setIsLeftPanelOpen,
    isLeftPanelOpen,
    panelPreferenceReady,
  } = chatState

  const surface = useChatSurface({
    activeChatId,
    adoptActiveChat,
    isLeftPanelOpen,
    panelPreferenceReady,
    setIsLeftPanelOpen,
    isBrandSwitching,
    initialChatSide,
    initialChatView,
  })

  const { user: clerkUser } = useUser()
  const currentUser = useMemo<ChatParticipant>(() => {
    if (clerkUser) {
      const nameInput = {
        firstName: clerkUser.firstName,
        lastName: clerkUser.lastName,
        fullName: clerkUser.fullName,
        emailAddress: clerkUser.primaryEmailAddress?.emailAddress,
        userId: clerkUser.id,
      }
      return {
        userId: clerkUser.id,
        displayName: getParticipantDisplayName(nameInput),
        initials: getParticipantInitials(nameInput),
        imageUrl: clerkUser.imageUrl,
      }
    }
    return {
      userId,
      displayName: getParticipantDisplayName({ userId }),
      initials: getParticipantInitials({ userId }),
    }
  }, [clerkUser, userId])

  const participants = useMemo(
    () => ({ [currentUser.userId]: currentUser }),
    [currentUser]
  )

  const {
    attachedEmails,
    attachEmail,
    attachManyEmails,
    detachEmail,
    clearAttached: clearAttachedEmails,
    isAttached: isEmailAttached,
    pruneAgainst: pruneAttachedEmails,
    setRemixSource: setChatRemixSource,
    clearRemixSource: clearChatRemixSource,
    clearAllRemixSources: clearAllChatRemixSources,
    moveStagedTo: moveStagedEmailsTo,
  } = useChatAttachedEmails({ orgId, brandId, chatId: activeChatId })

  const attachedEmailIds = useMemo(
    () => new Set(attachedEmails.map((e) => e.emailId)),
    [attachedEmails]
  )

  const {
    attachedImages,
    attachedImageUrls,
    attachImage: attachComposerImage,
    detachImage: detachComposerImage,
    clearAttachedImages,
    purgeImageUrl: purgeComposerImageUrl,
    moveStagedTo: moveStagedImagesTo,
  } = useChatAttachedImages({ orgId, brandId, chatId: activeChatId })

  // Open tabs are retained: the store's memory bound only ever drops a
  // chat the strip no longer shows.
  const { isChatOpen } = chatState
  const [composerUploads] = useState(() => {
    // The attachment lists storage refused (`unsaved-attachment-lists.ts`)
    // follow the same rule: their bound never drops an open chat's list.
    // `isChatOpen` is stable, so registering once is enough.
    retainUnsavedListsOfOpenChats(isChatOpen)
    return createComposerUploadsStore({ isRetained: isChatOpen })
  })

  // See `GlobalChatContextValue.startNewChat`. The moves run in the same
  // event as `addTab`, before its state update commits: the attachment
  // hooks still hold the SOURCE chat. The new chat reads its list back
  // whenever it becomes active (twice on the full page, which re-adopts the
  // old chat until the navigation lands), and a list storage refused is
  // kept in memory for that read (`unsaved-attachment-lists.ts`).
  const { addTab } = chatState
  const startNewChat = useCallback(() => {
    const fromChatId = activeChatId
    const chatId = addTab()
    if (fromChatId) {
      moveStagedEmailsTo(chatId)
      moveStagedImagesTo(chatId)
      composerUploads.move(fromChatId, chatId)
    }
    return chatId
  }, [
    activeChatId,
    addTab,
    moveStagedEmailsTo,
    moveStagedImagesTo,
    composerUploads,
  ])

  const elementEditTracker = useElementEditTracker()

  // One-shot composer seed (see GlobalChatContextValue docs). Plain React
  // state is enough: the seed only has to survive from the seeding
  // surface's mount until the matching chat thread's first edit/submit,
  // within the same provider lifetime.
  const [composerSeed, setComposerSeed] = useState<{
    chatId: string
    text: string
  } | null>(null)

  const seedComposer = useCallback((chatId: string, text: string) => {
    if (!chatId || !text) {
      return
    }
    setComposerSeed({ chatId, text })
  }, [])

  const clearComposerSeed = useCallback(() => {
    setComposerSeed(null)
  }, [])

  // Per-chat composer drafts (see GlobalChatContextValue docs). A ref map,
  // not state: only a mounting thread reads it, so per-keystroke writes
  // must not re-render the provider subtree.
  const composerDraftsRef = useRef<Map<string, string>>(new Map())

  const getComposerDraft = useCallback(
    (chatId: string) => composerDraftsRef.current.get(chatId) ?? '',
    []
  )

  const setComposerDraft = useCallback((chatId: string, text: string) => {
    if (!chatId) {
      return
    }
    if (text.length === 0) {
      composerDraftsRef.current.delete(chatId)
    } else {
      composerDraftsRef.current.set(chatId, text)
    }
  }, [])

  const value = useMemo<GlobalChatContextValue>(
    () => ({
      orgId,
      userId,
      brandId,
      chatState,
      startNewChat,
      currentUser,
      participants,
      attachedEmails,
      attachEmail,
      attachManyEmails,
      detachEmail,
      clearAttachedEmails,
      isEmailAttached,
      attachedEmailIds,
      pruneAttachedEmails,
      setChatRemixSource,
      clearChatRemixSource,
      clearAllChatRemixSources,
      attachedImages,
      attachedImageUrls,
      attachComposerImage,
      detachComposerImage,
      clearAttachedImages,
      purgeComposerImageUrl,
      elementEditTracker,
      composerSeed,
      seedComposer,
      clearComposerSeed,
      getComposerDraft,
      setComposerDraft,
      composerUploads,
      ...surface,
    }),
    [
      orgId,
      userId,
      brandId,
      chatState,
      startNewChat,
      currentUser,
      participants,
      attachedEmails,
      attachEmail,
      attachManyEmails,
      detachEmail,
      clearAttachedEmails,
      isEmailAttached,
      attachedEmailIds,
      pruneAttachedEmails,
      setChatRemixSource,
      clearChatRemixSource,
      clearAllChatRemixSources,
      attachedImages,
      attachedImageUrls,
      attachComposerImage,
      detachComposerImage,
      clearAttachedImages,
      purgeComposerImageUrl,
      elementEditTracker,
      composerSeed,
      seedComposer,
      clearComposerSeed,
      getComposerDraft,
      setComposerDraft,
      composerUploads,
      surface,
    ]
  )

  return (
    <GlobalChatContext.Provider value={value}>
      {/*
        Headless, error-boundaried self-heal: resolves each open tab's owning
        brand and prunes sibling-brand tabs (cross-brand bleed fix). Isolated so
        a `chatBrandMap` failure can never crash the chat. NOT mounted on
        excluded routes (/home) — the tab strip isn't visible there,
        so its `chatBrandMap` Convex subscription would be pure overhead on the
        hottest landing page; pruning defers to the first split/full-mode
        navigation, and `useCanvasChats`' own brand-ownership prune remains the
        localStorage-level backstop.
      */}
      {isExcludedChatPath(pathname ?? '') ? null : (
        <ForeignTabPruner
          orgId={orgId}
          brandId={brandId}
          openTabs={chatState.openTabs}
          onResolved={chatState.pruneTabs}
        />
      )}
      <ElementEditTrackerProvider tracker={elementEditTracker}>
        {children}
      </ElementEditTrackerProvider>
    </GlobalChatContext.Provider>
  )
}
